// src/context/AuthContext.tsx
import Loading from 'components/Loading';
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { authService } from 'services/authService';
import {
  getAuthToken,
  localStorageService,
  setAuthToken,
} from 'utils/localStorage';
import { persistSsoTokenFromUrl } from 'utils/ssoToken';

interface AuthContextType {
  isAuthenticated: boolean;
  login: (token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  useEffect(() => {
    const checkAuthStatus = async () => {
      persistSsoTokenFromUrl();
      const token = getAuthToken();

      if (!token) {
        setIsAuthenticated(false);
        setIsLoaded(true);
        return;
      }

      try {
        const isValid = await authService.verifyToken();

        if (isValid) {
          setIsAuthenticated(true);
        } else {
          localStorageService.removeItem('token');
          setIsAuthenticated(false);
        }
      } catch (error: any) {
        if (error?.status === 401) {
          // Token expiré côté GoodLoka → on efface et on déconnecte
          localStorageService.removeItem('token');
          setIsAuthenticated(false);
        } else {
          // Vraie erreur réseau (serveur down, timeout) → on conserve la session
          setIsAuthenticated(true);
        }
      }

      setIsLoaded(true);
    };

    checkAuthStatus();
  }, []);

  const login = (token: string) => {
    setAuthToken(token);
    setIsAuthenticated(true);
  };

  const logout = () => {
    setIsAuthenticated(false);
    localStorageService.removeItem('token');
  };

  if (!isLoaded) {
    return <Loading />;
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
